import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const ResetPasswordConfirmPage = () => {
  const { uid, token } = useParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    try {
      await axios.post(`${SERVER_URL}/api/auth/users/reset_password_confirm/`, {
        uid,
        token,
        new_password: newPassword,
      });
      setMessage("Password reset successfully! Redirecting to sign-in...");
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-0">
      <Helmet>
        <title>Reset Password – LingoLab</title>
        <meta
          name="description"
          content="Reset your LingoLab account password securely. Enter your new password to regain access to your account."
        />
        <meta name="robots" content="noindex, follow" />
        <link
          rel="canonical"
          href="https://lingolab.ca/reset-password-confirm"
        />
        <meta property="og:title" content="Reset Password – LingoLab" />
        <meta
          property="og:description"
          content="Reset your LingoLab account password securely. Enter your new password to regain access to your account."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://lingolab.ca/reset-password-confirm"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Reset Password – LingoLab" />
        <meta
          name="twitter:description"
          content="Reset your LingoLab account password securely. Enter your new password to regain access to your account."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-full sm:max-w-md">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
          Reset Password
        </h2>
        <form onSubmit={handleResetPassword}>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="newPassword"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoFocus
              autoComplete="new-password"
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="confirmPassword"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="new-password"
            />
          </div>
          {message && (
            <p
              className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base break-words"
              aria-live="polite"
            >
              {message}
            </p>
          )}
          {error && (
            <p
              className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base break-words"
              aria-live="polite"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 text-base"
            disabled={isLoading}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordConfirmPage;
